import React, { useEffect, useState } from "react";
import ReportHeading from "../Components/ReportHeadings/ReportHeadings";
import { motion } from "framer-motion";
import Seo from "../../components/SeoComponent/Seo";
import { ToastContainer } from "react-toastify";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import Loader from '../../components/Loader/Loader';
import {
  AddVendor,
  fetchVendor,
  ViewVendor,
  updateVendor,
  VendorStatusCodeReport,
  VendorStatusCodeCreate,
  VendorStatusCodeUpdate,
  VendorStatusCodeView
} from "../../services/VendorService/VendorService";

const initialForm = {
  name: "",
  status_id: "",
  base_prod_url: "",
  base_uat_url: "",
};

const initialVendorStatusForm = {
  status_code: "",
  vendor_id: ""
};

const isValidUrl = (s) => {
  if (!s) return true;
  try {
    new URL(s);
    return true;
  } catch {
    return false;
  }
};

const VendorMaster = () => {
  const [vendors, setVendors] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Vendor Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [addForm, setAddForm] = useState(initialForm);
  const [editForm, setEditForm] = useState(initialForm);
  const [addErrors, setAddErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});

  // Vendor Status Code Modal
  const [showStatusCodeModal, setShowStatusCodeModal] = useState(false);
  const [vendorStatusCodes, setVendorStatusCodes] = useState([]);
  const [statusForm, setStatusForm] = useState(initialVendorStatusForm);
  const [statusEditId, setStatusEditId] = useState(null);
  const [vendorIdForStatus, setVendorIdForStatus] = useState("");

  // Load vendors
  const loadVendors = async () => {
    setIsLoading(true);
    try {
      const res = await fetchVendor();
      const payload = res?.data ?? res;
      const list = payload?.data || payload?.vendors || (Array.isArray(payload) ? payload : []) || [];
      setVendors(list);

      const lastPage = payload?.last_page || payload?.meta?.last_page || 1;
      setTotalPages(lastPage);
    } catch (err) {
      showErrorToast("Failed to fetch vendors");
    } finally {
      setIsLoading(false);
    }
  };

  // Load vendor status codes
  const loadStatusCodes = async (vendorId) => {
    try {
      const res = await VendorStatusCodeReport({ vendor_id: vendorId });
      setVendorStatusCodes(res?.data || []);
    } catch (err) {
      showErrorToast("Failed to load status codes");
    }
  };

  useEffect(() => {
    loadVendors(page);
  }, [page]);

  // Validation
  const validateVendor = (data) => {
    const e = {};
    if (!data.name?.trim()) e.name = "Vendor name is required";
    if (!data.status_id) e.status_id = "Status is required";
    if (data.base_prod_url && !isValidUrl(data.base_prod_url)) e.base_prod_url = "Invalid Production URL";
    if (data.base_uat_url && !isValidUrl(data.base_uat_url)) e.base_uat_url = "Invalid UAT URL";
    return e;
  };

  // Vendor handlers
  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddForm((s) => ({ ...s, [name]: value }));
    if (addErrors[name]) setAddErrors({ ...addErrors, [name]: "" });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((s) => ({ ...s, [name]: value }));
    if (editErrors[name]) setEditErrors({ ...editErrors, [name]: "" });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const errors = validateVendor(addForm);
    setAddErrors(errors);
    if (Object.keys(errors).length) return;

    setIsLoading(true);
    try {
      await AddVendor(addForm);
      showSuccessToast("Vendor added successfully");
      setShowAddModal(false);
      setAddForm(initialForm);
      setPage(1);
      loadVendors(1);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to add vendor");
    } finally {
      setIsLoading(false);
    }
  };

  const openEdit = async (id) => {
    setIsLoading(true);
    try {
      const res = await ViewVendor({ id });
      const vendorData = res?.data?.data || res?.data || {};
      setEditId(id);
      setEditForm({
        name: vendorData.name ?? "",
        status_id: vendorData.status_id ?? "",
        base_prod_url: vendorData.base_prod_url ?? "",
        base_uat_url: vendorData.base_uat_url ?? "",
      });
      setEditErrors({});
      setShowEditModal(true);
    } catch (err) {
      showErrorToast("Failed to load vendor details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const errors = validateVendor(editForm);
    setEditErrors(errors);
    if (Object.keys(errors).length) return;

    setIsLoading(true);
    try {
      await updateVendor({ id: editId, payload: editForm });
      showSuccessToast("Vendor updated successfully");
      setShowEditModal(false);
      setEditId(null);
      loadVendors(page);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to update vendor");
    } finally {
      setIsLoading(false);
    }
  };

  // Status Code handlers
  const openStatusCodeModal = (vendorId = "") => {
    setVendorIdForStatus(vendorId);
    setStatusForm(initialVendorStatusForm);
    setStatusEditId(null);
    // if (vendorId) loadStatusCodes(vendorId);
    setShowStatusCodeModal(true);
  };

  const handleStatusChange = (e) => {
    const { name, value } = e.target;
    setStatusForm((s) => ({ ...s, [name]: value }));
  };

  const handleStatusSubmit = async () => {
    try {
      if (statusEditId) {
        await VendorStatusCodeUpdate({
          id: statusEditId,
          vendor_id: vendorIdForStatus,
          status_code: statusForm.status_code
        });
        showSuccessToast("Status code updated");
      } else {
        await VendorStatusCodeCreate({
          vendor_id: vendorIdForStatus,
          status_code: statusForm.status_code
        });
        showSuccessToast("Status code added");
      }
      loadStatusCodes(vendorIdForStatus);
    } catch (err) {
      showErrorToast("Status code operation failed");
    }
  };

  const openStatusEdit = async (id) => {
    try {
      const res = await VendorStatusCodeView({ id });
      setStatusEditId(id);
      setStatusForm({ status_code: res?.data?.status_code || "" });
    } catch (err) {
      showErrorToast("Failed to load status code");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="p-3 md:p-5 max-w-7xl mx-auto"
    >
      <Seo />
      {isLoading && <Loader />}
      <ToastContainer />
      
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-1 md:p-3">
        <ReportHeading name="Vendor Master" />

        {/* Controls */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-6">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
            >
              + Add Vendor
            </button>
          </div>

          {/* <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600 font-medium">
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1 || isLoading}
                className="px-2 py-2 border-2 border-gray-300 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-400 transition-all"
              >
                Prev
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || isLoading}
                className="px-4 py-2 border-2 border-gray-300 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-400 transition-all"
              >
                Next
              </button>
            </div>
          </div> */}
        </div>

        {/* Vendors Table */}
        <div className="overflow-x-auto mt-2 rounded-xl border border-gray-200 shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0">
              <tr>
                <th className="border-b px-2 py-1 text-left font-semibold text-gray-800">#</th>
                <th className="border-b px-2 py-1 text-left font-semibold text-gray-800">Name</th>
                <th className="border-b px-2 py-1 text-left font-semibold text-gray-800">Prod URL</th>
                <th className="border-b px-2 py-1 text-left font-semibold text-gray-800">UAT URL</th>
                <th className="border-b px-2 py-1 text-left font-semibold text-gray-800">Status</th>
                <th className="border-b px-2 py-1 text-left font-semibold text-gray-800">Created</th>
                <th className="border-b px-2 py-1 text-center font-semibold text-gray-800">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {vendors.length ? vendors.map((v, idx) => (
                <tr key={v.id ?? idx} className="hover:bg-gray-50 transition-colors">
                  <td className="px-2 py-1 font-medium text-gray-900">
                    {(page - 1) * 10 + idx + 1}
                  </td>
                  <td className="px-2 py-1 font-semibold text-gray-900 max-w-md truncate">
                    {v.name ?? "-"}
                  </td>
                  <td className="px-2 py-1 text-gray-700 max-w-xs truncate">
                    {v.base_prod_url ? (
                      <a href={v.base_prod_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                        {v.base_prod_url}
                      </a>
                    ) : "-"}
                  </td>
                  <td className="px-2 py-1 text-gray-700 max-w-xs truncate">
                    {v.base_uat_url ? (
                      <a href={v.base_uat_url} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline text-sm">
                        {v.base_uat_url}
                      </a>
                    ) : "-"}
                  </td>
                  <td className="px-2 py-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      v.status?.value === "active" 
                        ? "bg-green-100 text-green-800 border border-green-200" 
                        : "bg-red-100 text-red-800 border border-red-200"
                    }`}>
                      {v.status?.value || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-sm text-gray-600">
                    {v.created_at ? new Date(v.created_at).toLocaleDateString() : "-"}
                  </td>
                  <td className="px-2 py-1 text-center">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => openEdit(v.id)}
                        className="px-2 py-0.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium shadow-sm hover:shadow-md transition-all"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => openStatusCodeModal(v.id)}
                        className="px-2 py-0.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg font-medium shadow-sm hover:shadow-md transition-all"
                      >
                        Status Codes
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" className="px-2 py-6 text-center text-gray-500">
                    No vendors found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

         {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg w-full max-w-xl p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Add Vendor</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-600">✕</button>
            </div>

            <form onSubmit={handleAddSubmit} className="mt-3 space-y-3">
              <div>
                <label className="block text-sm mb-1">Name <span className="text-red-500">*</span></label>
                <input
                  name="name"
                  value={addForm.name}
                  onChange={handleAddChange}
                  className={`w-full border rounded px-3 py-2 text-sm ${addErrors.name ? "border-red-500" : "border-gray-300"}`}
                />
                {addErrors.name && <div className="text-xs text-red-500 mt-1">{addErrors.name}</div>}
              </div>

         <div>
  <label className="block text-sm mb-1">
    Status ID <span className="text-red-500">*</span>
  </label>

  <select
    name="status_id"
    value={addForm.status_id}
    onChange={handleAddChange}
    className={`w-full border rounded px-3 py-2 text-sm ${
      addErrors.status_id ? "border-red-500" : "border-gray-300"
    }`}
  >
    <option value="" selected>Select Status</option>
    <option value="5">Active</option>
    <option value="4">Inactive</option>
  </select>

  {addErrors.status_id && (
    <div className="text-xs text-red-500 mt-1">{addErrors.status_id}</div>
  )}
</div>

           

              <div>
                <label className="block text-sm mb-1">Base Prod URL</label>
                <input
                  name="base_prod_url"
                  value={addForm.base_prod_url}
                  onChange={handleAddChange}
                  className={`w-full border rounded px-3 py-2 text-sm ${addErrors.base_prod_url ? "border-red-500" : "border-gray-300"}`}
                />
                {addErrors.base_prod_url && <div className="text-xs text-red-500 mt-1">{addErrors.base_prod_url}</div>}
              </div>

              <div>
                <label className="block text-sm mb-1">Base UAT URL</label>
                <input
                  name="base_uat_url"
                  value={addForm.base_uat_url}
                  onChange={handleAddChange}
                  className={`w-full border rounded px-3 py-2 text-sm ${addErrors.base_uat_url ? "border-red-500" : "border-gray-300"}`}
                />
                {addErrors.base_uat_url && <div className="text-xs text-red-500 mt-1">{addErrors.base_uat_url}</div>}
              </div>

              <div className="flex justify-end gap-2 mt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-700 text-white rounded">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg w-full max-w-xl p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Edit Vendor</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-600">✕</button>
            </div>

            <form onSubmit={handleEditSubmit} className="mt-3 space-y-3">
              <div>
                <label className="block text-sm mb-1">Name <span className="text-red-500">*</span></label>
                <input
                  name="name"
                  value={editForm.name}
                  onChange={handleEditChange}
                  className={`w-full border rounded px-3 py-2 text-sm ${editErrors.name ? "border-red-500" : "border-gray-300"}`}
                />
                {editErrors.name && <div className="text-xs text-red-500 mt-1">{editErrors.name}</div>}
              </div>

<div>
  <label className="block text-sm mb-1">
    Status ID <span className="text-red-500">*</span>
  </label>

  <select
    name="status_id"
    value={editForm.status_id}
    onChange={handleEditChange}
    className={`w-full border rounded px-3 py-2 text-sm ${
      editErrors.status_id ? "border-red-500" : "border-gray-300"
    }`}
  >
    <option value="">Select Status</option>
    <option value="5">Active</option>
    <option value="4">Inactive</option>
  </select>

  {editErrors.status_id && (
    <div className="text-xs text-red-500 mt-1">{editErrors.status_id}</div>
  )}
</div>


              <div>
                <label className="block text-sm mb-1">Base Prod URL</label>
                <input
                  name="base_prod_url"
                  value={editForm.base_prod_url}
                  onChange={handleEditChange}
                  className={`w-full border rounded px-3 py-2 text-sm ${editErrors.base_prod_url ? "border-red-500" : "border-gray-300"}`}
                />
                {editErrors.base_prod_url && <div className="text-xs text-red-500 mt-1">{editErrors.base_prod_url}</div>}
              </div>

              <div>
                <label className="block text-sm mb-1">Base UAT URL</label>
                <input
                  name="base_uat_url"
                  value={editForm.base_uat_url}
                  onChange={handleEditChange}
                  className={`w-full border rounded px-3 py-2 text-sm ${editErrors.base_uat_url ? "border-red-500" : "border-gray-300"}`}
                />
                {editErrors.base_uat_url && <div className="text-xs text-red-500 mt-1">{editErrors.base_uat_url}</div>}
              </div>

              <div className="flex justify-end gap-2 mt-2">
                <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-700 text-white rounded">Update</button>
              </div>
            </form>
          </div>
        </div>
      )}

        {/* NEW: Vendor Status Code Modal */}
        {showStatusCodeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2">
            <div className="bg-white rounded-2xl w-full max-w-md max-h-[82vh] overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-white p-1 border-b rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-md font-bold text-gray-900">Status Codes</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Vendor ID: <span className="font-mono bg-blue-100 px-2 py-1 rounded text-sm">{vendorIdForStatus}</span>
                    </p>
                  </div>
                  <button 
                    onClick={() => setShowStatusCodeModal(false)}
                    className="text-red-500 hover:text-gray-700 text-2xl font-bold"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-2 space-y-2">
                {/* Status Code Form */}
                <div className="bg-gray-50 p-2 rounded-xl border">
                  <h4 className="font-semibold text-md mb-1 text-gray-800">Manage Status Code <span className="text-red-600">*</span>  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                
                    <input
                      type="text"
                      name="status_code"
                      placeholder="Status Code (e.g., 404, 500)"
                      value={statusForm.status_code}
                      onChange={handleStatusChange}
                      className="w-full border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={handleStatusSubmit}
                    className="w-1/3 mt-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-1 rounded-md shadow-lg transition-all"
                  >
                    {statusEditId ? "Update Status Code" : "Add Status Code"}
                  </button>
                </div>

                {/* Status Codes List */}
                <div>
                  <h4 className="font-semibold text-md mb-2 text-gray-800">Status Codes ({vendorStatusCodes.length})</h4>
                  <div className="grid gap-1">
                    {vendorStatusCodes.map((status) => (
                      <div key={status.id} className="flex items-center justify-between p-2 bg-white border rounded-xl hover:shadow-md transition-all">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-md bg-blue-600 px-2 py-1 rounded-xl text-white shadow-lg">
                            {status.status_code}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openStatusEdit(status.id)}
                            className="px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-sm rounded-md font-medium"
                          >
                            Edit
                          </button>
                         
                        </div>
                      </div>
                    ))}
                    {vendorStatusCodes.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        No status codes found. Add one above!
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
      </div>
    </motion.div>
  );
};

export default VendorMaster;
